import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { BrandCreateRequest } from '@/api/handlers/resources/brands/requests';
import type { BrandWriteResponse } from '@/api/handlers/resources/brands/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BrandQueryKeys } from './brandQueryKeys';

/**
 * Mutation behind the Add Brand submit. The bos-api takes the cropper payload
 * server-side (multipart) so the round-trip is a single POST. The list is
 * server-paginated and server-ordered (by name), so where the new brand lands
 * is the server's call — we invalidate the cached pages and let them refetch
 * rather than guessing a position with an optimistic insert.
 */
export const useCreateBrandMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<BrandWriteResponse>,
        Error,
        BrandCreateRequest
    >({
        mutationFn: (body) => apiClient.brands.createBrand(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: BrandQueryKeys.list(),
                });
            }
        },
    });
};
